import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { toast } from "sonner";
import { UserPlus, CheckSquare, Square } from "lucide-react";

const AVAILABLE_PERMISSIONS = [
  { id: "view_all_orders", label: "View All Orders", description: "Can see all orders in the system" },
  { id: "view_own_orders", label: "View Own Orders", description: "Can only see their own orders" },
  { id: "create_orders", label: "Create Orders", description: "Can submit new part orders" },
  { id: "edit_orders", label: "Edit Orders", description: "Can modify existing orders" },
  { id: "delete_orders", label: "Delete Orders", description: "Can delete orders" },
  { id: "update_status", label: "Update Order Status", description: "Can change order status" },
  { id: "assign_orders", label: "Assign Orders to Techs", description: "Can assign orders to technicians" },
  { id: "manage_users", label: "Manage Users", description: "Can create, edit, and delete users" },
  { id: "view_pricing", label: "View Full Pricing", description: "Can see markup and pricing details" },
  { id: "update_tracking", label: "Update Tracking Info", description: "Can add tracking numbers and ETAs" }
];

const ROLE_PRESETS = {
  admin: ["view_all_orders", "create_orders", "edit_orders", "delete_orders", "update_status", "assign_orders", "manage_users", "view_pricing", "update_tracking"],
  part_manager: ["view_all_orders", "create_orders", "edit_orders", "update_status", "assign_orders", "view_pricing", "update_tracking"],
  dispatcher: ["view_all_orders", "create_orders", "update_status", "assign_orders", "update_tracking"],
  tech: ["view_own_orders", "create_orders"]
};

export default function CreateTechForm({ onTechCreated }) {
  const [formData, setFormData] = useState({
    full_name: "",
    username: "",
    email: "",
    password: "",
    role: "tech"
  });
  const [selectedPermissions, setSelectedPermissions] = useState(ROLE_PRESETS.tech);
  const [loading, setLoading] = useState(false);

  const togglePermission = (permissionId) => {
    setSelectedPermissions(prev => {
      if (prev.includes(permissionId)) {
        return prev.filter(p => p !== permissionId);
      } else {
        return [...prev, permissionId];
      }
    });
  };

  const handleRoleChange = (role) => {
    setFormData({ ...formData, role });
    setSelectedPermissions(ROLE_PRESETS[role] || []);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedPermissions.length === 0) {
      toast.error("Please select at least one permission");
      return;
    }
    setLoading(true);
    try {
      console.log("Creating user...");
      await axios.post("/auth/create-user", {
        ...formData,
        permissions: selectedPermissions
      });
      toast.success(`User account created successfully!`);
      setFormData({
        full_name: "",
        username: "",
        email: "",
        password: "",
        role: "tech"
      });
      setSelectedPermissions(ROLE_PRESETS.tech);
      onTechCreated();
    } catch (error) {
      console.error("Error creating user:", error);
      toast.error(error.response?.data?.detail || "Failed to create user account");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="max-w-3xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <UserPlus className="w-5 h-5" />
          Create User Account
        </CardTitle>
        <CardDescription>Create a new user account with custom permissions</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="full_name">Full Name *</Label>
              <Input
                id="full_name"
                data-testid="create-tech-fullname-input"
                type="text"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                required
                className="mt-1"
                placeholder="John Doe"
              />
            </div>
            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                data-testid="create-tech-email-input"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="mt-1"
                placeholder="john@example.com"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="username">Username *</Label>
              <Input
                id="username"
                data-testid="create-tech-username-input"
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                required
                className="mt-1"
                placeholder="johndoe"
              />
            </div>
            <div>
              <Label htmlFor="password">Password *</Label>
              <Input
                id="password"
                data-testid="create-tech-password-input"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                className="mt-1"
                placeholder="••••••••"
              />
            </div>
          </div>
          
          <div>
            <Label htmlFor="role">Role Preset *</Label>
            <select
              id="role"
              value={formData.role}
              onChange={(e) => handleRoleChange(e.target.value)}
              className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md"
              required
            >
              <option value="tech">Technician</option>
              <option value="dispatcher">Dispatcher</option>
              <option value="part_manager">Part Manager</option>
              <option value="admin">Admin</option>
            </select>
            <p className="text-xs text-slate-500 mt-1">Select a role preset, then customize permissions below</p>
          </div>
          
          {/* Permissions Selector */}
          <div>
            <Label className="text-base font-semibold mb-3 block">Permissions ({selectedPermissions.length} selected) *</Label>
            <div className="border border-slate-200 rounded-lg p-4 bg-slate-50 max-h-80 overflow-y-auto">
              <div className="space-y-3">
                {AVAILABLE_PERMISSIONS.map((permission) => (
                  <div
                    key={permission.id}
                    onClick={() => togglePermission(permission.id)}
                    className="flex items-start gap-3 p-3 bg-white rounded-md border border-slate-200 cursor-pointer hover:bg-slate-50 transition-colors"
                  >
                    <div className="mt-0.5">
                      {selectedPermissions.includes(permission.id) ? (
                        <CheckSquare className="w-5 h-5 text-violet-600" />
                      ) : (
                        <Square className="w-5 h-5 text-slate-400" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-900">{permission.label}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{permission.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <Button
            type="submit"
            className="w-full"
            disabled={loading}
            data-testid="create-tech-submit-button"
          >
            {loading ? "Creating Account..." : `Create User Account`}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
